import { useState, useEffect } from 'react';
import { Eye, EyeOff, CreditCard as Edit, Plus, Search, Filter, Zap, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useModels } from '../hooks/useModels';
import { supabase } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

type FilterType = 'all' | 'available' | 'unavailable' | 'featured' | 'coming_soon';
type CategoryFilter = 'all' | 'models' | 'utils' | 'effects';

export const ModelsPage = () => {
  const navigate = useNavigate();
  const { models, loading, error, refetch } = useModels();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [updatingModels, setUpdatingModels] = useState<Set<string>>(new Set());
  const [duplicatingModel, setDuplicatingModel] = useState<string | null>(null);
  const [accessRecord, setAccessRecord] = useState<any>(null);

  useEffect(() => {
    setAccessRecord(parseAdminStaffSession());
  }, []);

  const handleToggleAvailability = async (modelId: string, currentStatus: boolean) => {
    // Check permissions
    if (accessRecord && accessRecord.role !== 'superadmin') {
      const model = models.find(m => m.model_id === modelId);
      if (model && model.hy_user_id !== accessRecord.hy_user_id) {
        alert('Access denied. You can only modify your own models.');
        return;
      }
    }

    setUpdatingModels(prev => new Set([...prev, modelId]));

    try {
      const { error } = await supabase
        .from('modelconfig')
        .update({ is_available: !currentStatus })
        .eq('model_id', modelId);

      if (error) {
        throw error;
      }

      // Refresh the models list
      await refetch();
    } catch (error) {
      console.error('Error updating model status:', error);
      alert('Failed to update model status');
    } finally {
      setUpdatingModels(prev => {
        const newSet = new Set(prev);
        newSet.delete(modelId);
        return newSet;
      });
    }
  };

  const handleEditModel = (modelId: string) => {
    // Check permissions
    if (accessRecord && accessRecord.role !== 'superadmin') {
      const model = models.find(m => m.model_id === modelId);
      if (model && model.hy_user_id !== accessRecord.hy_user_id) {
        alert('Access denied. You can only edit your own models.');
        return;
      }
    }

    // Navigate to create page with edit mode
    navigate(`/admin/create?edit=${modelId}`);
  };

  const handleCreateNew = () => {
    navigate('/admin/create');
  };

  const handleDuplicateModel = async (modelId: string) => {
    // Check permissions
    if (accessRecord && accessRecord.role !== 'superadmin') {
      const model = models.find(m => m.model_id === modelId);
      if (model && model.hy_user_id !== accessRecord.hy_user_id) {
        alert('Access denied. You can only duplicate your own models.');
        return;
      }
    }

    if (!confirm('Are you sure you want to duplicate this model?')) {
      return;
    }

    setDuplicatingModel(modelId);

    try {
      // Fetch the original model
      const { data: originalModel, error: fetchError } = await supabase
        .from('modelconfig')
        .select('*')
        .eq('model_id', modelId)
        .single();

      if (fetchError || !originalModel) {
        throw new Error('Failed to fetch original model');
      }

      // Create new model data
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '-');
      const newModelId = `${originalModel.model_id}-copy-${timestamp}`;
      
      const duplicatedModel = {
        ...originalModel,
        id: undefined, // Let database generate new UUID
        model_id: newModelId,
        name: `${originalModel.name} (Copy)`,
        is_available: false, // Start as disabled for safety
        featured: false, // Remove featured status
        route: null, // Clear custom route to avoid conflicts
        sort_order: (originalModel.sort_order || 0) + 1,
        created_at: undefined, // Let database set current timestamp
        updated_at: undefined // Let database set current timestamp
      };

      // Insert the duplicated model
      const { error: insertError } = await supabase
        .from('modelconfig')
        .insert(duplicatedModel);

      if (insertError) {
        throw insertError;
      }

      // Refresh models list
      await refetch();
      
      alert('Model duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating model:', error);
      alert('Failed to duplicate model. Please try again.');
    } finally {
      setDuplicatingModel(null);
    }
  };

  // Filter models based on search and filters
  const filteredModels = models.filter(model => {
    // Apply user-level filtering for non-superadmin users
    const userCanAccess = !accessRecord || 
      accessRecord.role === 'superadmin' || 
      model.hy_user_id === accessRecord.hy_user_id;
    
    if (!userCanAccess) {
      return false;
    }

    // Search filter
    const matchesSearch = !searchTerm.trim() ||
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (model.tags && model.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));

    // Status filter
    const matchesFilter = filter === 'all' ||
      (filter === 'available' && model.is_available) ||
      (filter === 'unavailable' && !model.is_available) ||
      (filter === 'featured' && model.featured) ||
      (filter === 'coming_soon' && model.coming_soon);

    // Category filter
    const matchesCategory = categoryFilter === 'all' || model.category === categoryFilter;

    return matchesSearch && matchesFilter && matchesCategory;
  });

  const getStatusColor = (model: any) => {
    if (!model.is_available) return 'text-status-error';
    if (model.coming_soon) return 'text-status-warning';
    if (model.featured) return 'text-accent';
    return 'text-status-success';
  };

  const getStatusLabel = (model: any) => {
    if (!model.is_available) return 'Disabled';
    if (model.coming_soon) return 'Coming Soon';
    if (model.featured) return 'Featured';
    return 'Active';
  };

  const filterOptions = [
    { id: 'all', label: 'All Models', count: models.length },
    { id: 'available', label: 'Available', count: models.filter(m => m.is_available).length },
    { id: 'unavailable', label: 'Disabled', count: models.filter(m => !m.is_available).length },
    { id: 'featured', label: 'Featured', count: models.filter(m => m.featured).length },
    { id: 'coming_soon', label: 'Coming Soon', count: models.filter(m => m.coming_soon).length }
  ];

  const categoryOptions = [
    { id: 'all', label: 'All Categories', count: models.length },
    { id: 'models', label: 'Models', count: models.filter(m => m.category === 'models').length },
    { id: 'utils', label: 'Utils', count: models.filter(m => m.category === 'utils').length },
    { id: 'effects', label: 'Effects', count: models.filter(m => m.category === 'effects').length }
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar title="Model Management" subtitle="Manage AI model configurations">
        <button
          type="button"
          onClick={handleCreateNew}
          className="flex items-center space-x-2 px-4 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand-hover transition-all"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Model</span>
        </button>
      </AdminPageToolbar>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 pb-8">
        {/* Search and Filters */}
        <div className="space-y-4 mb-8">
          {/* Search Bar */}
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-th-secondary" />
              </div>
              <input
                type="text"
                placeholder="Search models by name, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-lg text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 transition-all"
              />
            </div>
          </div>

          {/* Filter Chips */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-th-secondary" />
            <span className="text-sm text-th-secondary">Status:</span>
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setFilter(option.id as FilterType)}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  filter === option.id 
                    ? 'bg-brand text-surface shadow-lg shadow-primary/25'
                    : 'bg-elevated text-th-secondary hover:text-th-primary hover:bg-overlay border border-subtle'
                }`}
              >
                <span>{option.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  filter === option.id 
                    ? 'bg-white/20 text-white' 
                    : 'bg-brand/20 text-brand'
                }`}>
                  {option.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-th-secondary">Category:</span>
            {categoryOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setCategoryFilter(option.id as CategoryFilter)}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  categoryFilter === option.id 
                    ? 'bg-accent text-surface shadow-lg shadow-secondary/25'
                    : 'bg-elevated text-th-secondary hover:text-th-primary hover:bg-overlay border border-subtle'
                }`}
              >
                <span>{option.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  categoryFilter === option.id 
                    ? 'bg-white/20 text-white' 
                    : 'bg-accent/20 text-accent'
                }`}>
                  {option.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Models List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-th-secondary">Loading models...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4 max-w-md">
              <AlertCircle className="w-16 h-16 text-status-error mx-auto" />
              <h3 className="text-xl font-bold text-th-primary">Failed to Load Models</h3>
              <p className="text-th-secondary">{error}</p>
              <button onClick={refetch} className="button-primary">
                Try Again
              </button>
            </div>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-12">
            <div className="space-y-6">
              <div className="w-24 h-24 bg-brand rounded-full flex items-center justify-center mx-auto">
                <Zap className="w-12 h-12 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-th-primary mb-2">
                  {searchTerm ? 'No Models Found' : 'No Models Yet'}
                </h3>
                <p className="text-th-secondary">
                  {searchTerm 
                    ? `No models match your search "${searchTerm}"`
                    : 'Create your first AI model configuration to get started'
                  }
                </p>
              </div>
              <button onClick={handleCreateNew} className="button-primary">
                Create New Model
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredModels.map((model) => {
              const isUpdating = updatingModels.has(model.model_id);
              
              return (
                <div 
                  key={model.model_id}
                  className="card p-4 hover:bg-overlay transition-all"
                >
                  <div className="flex items-center justify-between">
                    {/* Model Info - Compact Single Row */}
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      {/* Icon */}
                      <div className={`p-1.5 rounded ${model.icon_bg} flex-shrink-0`}>
                        <Zap className={`w-4 h-4 ${model.icon_color}`} />
                      </div>
                      
                      {/* Name and Status */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-base font-semibold text-th-primary truncate">
                            {model.name}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${getStatusColor(model)} bg-current/10 flex-shrink-0`}>
                            {getStatusLabel(model)}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-elevated border border-subtle rounded text-th-secondary font-medium flex-shrink-0">
                            {model.category}
                          </span>
                        </div>
                      </div>
                      
                      {/* Quick Stats */}
                      <div className="hidden md:flex items-center space-x-6 text-xs text-th-secondary flex-shrink-0">
                        <span className="truncate max-w-[120px]">ID: {model.model_id}</span>
                        <span>{model.credit_cost} credits</span>
                        <span className="uppercase">{model.output_type}</span>
                        {model.estimated_time && (
                          <span>~{model.estimated_time}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions - Compact */}
                    <div className="flex items-center space-x-1 ml-4 flex-shrink-0">
                      {/* Duplicate Model */}
                      <button
                        onClick={() => handleDuplicateModel(model.model_id)}
                        disabled={duplicatingModel === model.model_id}
                        className="flex items-center space-x-1 px-2 py-1.5 bg-status-warning/20 border border-status-warning/30 text-status-warning hover:bg-status-warning/30 rounded text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Duplicate Model"
                      >
                        {duplicatingModel === model.model_id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                          </svg>
                        )}
                        <span className="hidden lg:inline">
                          Duplicate
                        </span>
                      </button>

                      {/* Toggle Availability */}
                      <button
                        onClick={() => handleToggleAvailability(model.model_id, model.is_available)}
                        disabled={isUpdating}
                        className={`flex items-center space-x-1 px-2 py-1.5 rounded text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          model.is_available
                            ? 'bg-status-error/20 border border-status-error/30 text-status-error hover:bg-status-error/30'
                            : 'bg-status-success/20 border border-status-success/30 text-status-success hover:bg-status-success/30'
                        }`}
                        title={model.is_available ? 'Deactivate Model' : 'Activate Model'}
                      >
                        {isUpdating ? (
                          <LoadingSpinner size="sm" />
                        ) : model.is_available ? (
                          <EyeOff className="w-3 h-3" />
                        ) : (
                          <Eye className="w-3 h-3" />
                        )}
                        <span className="hidden lg:inline">
                          {model.is_available ? 'Deactivate' : 'Activate'}
                        </span>
                      </button>

                      {/* Edit Model */}
                      <button
                        onClick={() => handleEditModel(model.model_id)}
                        className="flex items-center space-x-1 px-2 py-1.5 bg-accent/20 border border-accent/30 text-accent hover:bg-accent/30 rounded text-xs font-medium transition-all"
                        title="Edit Model Configuration"
                      >
                        <Edit className="w-3 h-3" />
                        <span className="hidden lg:inline">Edit</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Results Summary */}
        {!loading && !error && (
          <div className="mt-8 pt-4 border-t border-subtle text-center">
            <p className="text-th-secondary text-sm">
              Showing {filteredModels.length} of {models.length} model{models.length !== 1 ? 's' : ''}
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
          </div>
        )}
        </div>
      </main>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
};